import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DeviceProvider } from './contexts/DeviceContext';
import { MusicProvider, useMusic } from './contexts/MusicContext';
import { RemoteBusProvider } from './contexts/RemoteBusContext';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ClientDashboardPage from './pages/ClientDashboardPage';
import ControlBoothPage from './pages/ControlBoothPage';
import PlaylistsPage from './pages/PlaylistsPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import PlaylistPage from './pages/PlaylistPage';
import AdminClientsPage from './pages/AdminClientsPage';
import AdminPlaylistsPage from './pages/AdminPlaylistsPage';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import VenueLandingPage from './pages/VenueLandingPage';
import SettingsPage from './pages/SettingsPage';
import MenuAdminPage from './pages/MenuAdminPage';
import AdminClientMenuPage from './pages/AdminClientMenuPage';
import AdminClientSchedulePage from './pages/AdminClientSchedulePage';
import GuestRequestPage from './pages/GuestRequestPage';
import GuestMenuPage from './pages/GuestMenuPage';
import GuestReviewPage from './pages/GuestReviewPage';
import TVDisplayPage from './pages/TVDisplayPage';
import ReviewsAdminPage from './pages/ReviewsAdminPage';
import GamesAdminPage from './pages/GamesAdminPage';
import GuestGamesPage from './pages/GuestGamesPage';
import DJConsolePage from './pages/DJConsolePage';
import StaffPage from './pages/StaffPage';
import EventsPage from './pages/EventsPage';
import BoothPreview from './pages/BoothPreview';
import AdminForensicsPage from './pages/AdminForensicsPage';
import ClientOrdersPage from './pages/ClientOrdersPage';
import GuestsPage from './pages/GuestsPage';
import GuestEventPage from './pages/GuestEventPage';
import SplashScreen from './components/SplashScreen';
import VenueEventsListPage from './pages/VenueEventsListPage';
import { PWAInstallBanner } from './components/PWAInstall';
import { ConnectivityPill } from './components/ConnectivityPill';
import { installAxiosDefaults } from './utils/axiosSetup';
import './App.css';

// Global axios config (15 s timeout, credentials, network-status events)
// MUST run before any page-level axios call — this module is imported first.
installAxiosDefaults();

const ProtectedRoute = ({ children, superAdminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (superAdminOnly && user.role !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const HomeRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'superadmin') {
    return <Navigate to="/admin" replace />;
  }

  // The Control Booth is the new venue home (Feb 2026). The metrics that
  // used to live here now live at /analytics.
  return <ControlBoothPage />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      
      <Route path="/" element={<HomeRedirect />} />

      {/* Analytics — the old venue dashboard metrics moved here Feb 2026 so the
          Home / (Control Booth) can focus on real-time playback control. */}
      <Route path="/analytics" element={
        <ProtectedRoute>
          <ClientDashboardPage />
        </ProtectedRoute>
      } />

      <Route path="/playlist/:id" element={
        <ProtectedRoute>
          <PlaylistPage />
        </ProtectedRoute>
      } />
      <Route path="/playlists" element={
        <ProtectedRoute>
          <PlaylistsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute superAdminOnly>
          <AdminDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/clients" element={
        <ProtectedRoute superAdminOnly>
          <AdminClientsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/clients/:clientId/menu" element={
        <ProtectedRoute superAdminOnly>
          <AdminClientMenuPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/clients/:clientId/schedule" element={
        <ProtectedRoute superAdminOnly>
          <AdminClientSchedulePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/playlists" element={
        <ProtectedRoute superAdminOnly>
          <AdminPlaylistsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute superAdminOnly>
          <AdminAnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/forensics" element={
        <ProtectedRoute superAdminOnly>
          <AdminForensicsPage />
        </ProtectedRoute>
      } />
      {/* Legacy redirect — old bookmarks to /admin/telemetry land on the new analytics dashboard. */}
      <Route path="/admin/telemetry" element={<Navigate to="/admin/analytics" replace />} />
      <Route path="/admin/requests" element={
        <ProtectedRoute superAdminOnly>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      {/* Settings (formerly Requests) - accessible by both admin and client */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/requests" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />

      {/* DJ Console - accessible by both admin and client */}
      <Route path="/dj-console" element={
        <ProtectedRoute>
          <DJConsolePage />
        </ProtectedRoute>
      } />

      {/* Booth Preview — visual mock for design review, no live data */}
      <Route path="/booth-preview" element={
        <ProtectedRoute>
          <BoothPreview />
        </ProtectedRoute>
      } />

      {/* Guests — verified OTP scans + CSV export */}
      <Route path="/guests" element={
        <ProtectedRoute>
          <GuestsPage />
        </ProtectedRoute>
      } />

      {/* Menu admin (host CRUD) */}
      <Route path="/menu" element={
        <ProtectedRoute>
          <MenuAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/reviews" element={
        <ProtectedRoute>
          <ReviewsAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/games" element={
        <ProtectedRoute>
          <GamesAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/events" element={
        <ProtectedRoute>
          <EventsPage />
        </ProtectedRoute>
      } />
      <Route path="/orders" element={
        <ProtectedRoute>
          <ClientOrdersPage />
        </ProtectedRoute>
      } />
      
      {/* Public guest request page - no auth */}
      <Route path="/request/:code" element={<GuestRequestPage />} />
      <Route path="/v/:venueCode" element={<VenueLandingPage />} />
      <Route path="/menu/:code" element={<GuestMenuPage />} />
      <Route path="/review/:code" element={<GuestReviewPage />} />
      <Route path="/play/:code" element={<GuestGamesPage />} />
      <Route path="/event/:shareCode" element={<GuestEventPage />} />
      <Route path="/v/:venueCode/events" element={<VenueEventsListPage />} />
      <Route path="/tv/:code" element={<TVDisplayPage />} />
      {/* Permanent TV stream URL — resolved by venue_code, works on any smart TV 24/7 */}
      <Route path="/:venueCode/tv" element={<TVDisplayPage />} />
      {/* Dedicated DJ Hand-off mobile mode (PIN auth, scoped UI).
          /dj-handoff/:venueCode is the QR-scan target that pre-fills venue code;
          /staff kept as a legacy fallback for anyone bookmarked to the old URL. */}
      <Route path="/dj-handoff" element={<StaffPage />} />
      <Route path="/dj-handoff/:venueCode" element={<StaffPage />} />
      <Route path="/staff" element={<StaffPage />} />
    </Routes>
  );
}

// Bridge component — consumes MusicContext and hands a `dispatch` fn to the
// RemoteBus, which invokes it whenever a real-time command arrives over WS.
// Only the commands MusicContext actually exposes today are wired here;
// anything else (set_volume, seek, load_playlist) falls through to the
// existing 1s polling fallback in DJ Console so nothing regresses.
const RemoteBusBridge = ({ children }) => {
  const music = useMusic();
  const dispatch = React.useCallback((command /* , payload */) => {
    switch (command) {
      case 'play':  music.play?.(); break;
      case 'pause': music.pause?.(); break;
      case 'next':  music.nextTrack?.(); break;
      case 'prev':  music.previousTrack?.(); break;
      default: /* handled by REST polling fallback */ break;
    }
  }, [music]);
  return <RemoteBusProvider dispatch={dispatch}>{children}</RemoteBusProvider>;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DeviceProvider>
          <MusicProvider>
            <RemoteBusBridge>
              <SplashScreen />
              <AppRoutes />
              <PWAInstallBanner />
              <ConnectivityPill variant="floating" />
              <Toaster position="top-right" />
            </RemoteBusBridge>
          </MusicProvider>
        </DeviceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
